import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { message } from 'antd';

import CustomModal from '../../../components/atoms/CustomModal';
import FormField from '../../../components/FormField/FormField';
import Button from '../../../components/atoms/Button';
import { useCloseSupportTicketMutation } from '../../../store/services/SupportService';

interface CloseTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string | null;
}

export const CloseTicketModal: React.FC<CloseTicketModalProps> = ({
  isOpen,
  onClose,
  ticketId,
}) => {
  const { t } = useTranslation();
  const [closeTicket, { isLoading: isClosingTicket }] =
    useCloseSupportTicketMutation();

  const schema = z.object({
    reason: z
      .string()
      .trim()
      .min(
        50,
        t('components.validations.minLength', {
          value: 50,
          defaultValue: 'Minimum character 50',
        })
      )
      .max(
        1000,
        t('components.validations.maxLength', {
          value: 1000,
          defaultValue: 'Maximum character 1000',
        })
      ),
  });

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: { reason: '' },
  });

  const handleModalClose = () => {
    methods.reset();
    onClose();
  };

  const onSubmit = async (formData: { reason: string }) => {
    if (!ticketId) return;
    try {
      await closeTicket({
        closeMsg: formData.reason,
        ticketId,
      }).unwrap();
      message.success(
        t('support.serviceUpdatedSuccessfully', 'Ticket closed successfully')
      );
      handleModalClose();
    } catch (error: any) {
      console.error('Failed to close ticket:', error);
      message.error(
        error?.data?.message || t('somethingWentWrong', 'Something went wrong')
      );
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={t('support.close_ticket', 'Close ticket')}
      subTitle={t(
        'support.closeTicketDesc',
        'send reason to user and close ticket'
      )}
      width={500}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="mt-4 flex flex-col gap-4"
        >
          <FormField
            id="reason"
            name="reason"
            type="textarea"
            label={t('support.closeMsg', 'Message')}
            placeholder={t('support.TypecloseMsg', 'Enter message...')}
            minLength={50}
            maxLength={1000}
            showCount
            helperText={t('support.MinAndMax_Char', {
              min: 50,
              max: 1000,
              defaultValue: 'Minimum character 50 and the max. 1000',
            })}
            labelClassName="!text-base !font-medium !leading-6 !text-[#111928]"
            className="!rounded-xl"
          />
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              type="button"
              className="rounded-lg"
              onClick={handleModalClose}
              disabled={isClosingTicket}
            >
              {t('support.cancel', 'Cancel')}
            </Button>
            <Button
              variant="dark"
              type="submit"
              className="rounded-lg w-44"
              loading={isClosingTicket}
              disabled={isClosingTicket}
            >
              {t('support.save', 'Save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
};

export default CloseTicketModal;
